import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useConfig } from '../config/ConfigContext';
import useMediaQuery from '../hooks/useMediaQuery';
import useShortsData from './Shorts/hooks/useShortsData';
import useClipsData from './LectureClips/hooks/useClipsData';
import ShortCard from './ShortCard';
import { getDailyRandomSubset } from '../utils/dailyRandom';

/**
 * Component that displays a horizontal row of shorts and lecture clips
 * @param {Object} props
 * @param {string} props.googleSheetId - Google Sheet ID for fetching shorts
 * @returns {JSX.Element}
 */
const ShortsPanel = ({ googleSheetId }) => {
  const { theme, config } = useConfig();
  const rowRef = useRef(null);
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Fetch YouTube shorts data
  const {
    shorts,
    loading: shortsLoading,
    error: shortsError
  } = useShortsData(googleSheetId, 'all', config);

  // Fetch lecture clips from Supabase (if enabled)
  const clipsEnabled = config?.lecture_clips_enabled === true;
  const {
    clips: lectureClips,
    loading: clipsLoading,
    error: clipsError
  } = useClipsData('all', clipsEnabled ? config?.center_id : null);

  const loading = shortsLoading || (clipsEnabled && clipsLoading);
  const error = shortsError && (!clipsEnabled || clipsError);

  // Normalize lecture clips to match ShortCard's expected shape
  const normalizedClips = (clipsEnabled ? lectureClips : []).map(clip => ({
    ...clip,
    id: clip.id,
    title: clip.title,
    speakerName: clip.speakerName || clip.speaker,
    eventName: clip.series || clip.eventName,
    shortUrl: null, // No YouTube URL
    driveFileId: clip.driveFileId,
    isClip: true, // Flag to distinguish from YouTube shorts
  }));

  // Interleave: pick from shorts and clips alternating, clips first for variety
  const maxShorts = isMobile ? 4 : 15;
  const randomShorts = getDailyRandomSubset(shorts, maxShorts);
  const randomClips = getDailyRandomSubset(normalizedClips, Math.min(normalizedClips.length, isMobile ? 2 : 5));

  // Merge: clips at front, then shorts
  const displayItems = [...randomClips, ...randomShorts].slice(0, maxShorts);

  const hasClips = normalizedClips.length > 0;
  const hasShorts = shorts.length > 0;

  console.log('[ShortsPanel] Display:', displayItems.length,
    `(${randomClips.length} clips + ${randomShorts.length} shorts)`);
  
  // Scroll the row to the right
  const scrollRight = () => {
    if (rowRef.current) {
      const scrollAmount = isMobile ? 176 : 176; // Card width + gap (160px + 16px)
      rowRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  // Scroll the row to the left
  const scrollLeft = () => {
    if (rowRef.current) {
      const scrollAmount = isMobile ? 176 : 176; // Card width + gap (160px + 16px)
      rowRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    }
  };
  
  // Don't render if nothing to show or still loading
  if (loading || error || displayItems.length === 0) {
    return null;
  }

  const linkStyle = {
    color: theme.colors.linkColor || theme.colors.primary,
    textDecoration: 'none',
    fontWeight: '500',
    fontSize: isMobile ? '12px' : '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    transition: 'all 0.2s ease',
    opacity: 0.9
  };

  return (
    <div style={{ marginTop: '0', marginBottom: '40px' }}>
      {/* Section Header - Outside the frame */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        paddingLeft: isMobile ? '0' : '4px',
        paddingRight: isMobile ? '0' : '4px'
      }}>
        <div>
          <h2 style={{
            fontSize: isMobile ? '20px' : '24px',
            fontWeight: '600',
            margin: 0,
            color: theme?.colors?.headingColor || theme?.colors?.primary || '#0f172a'
          }}>
            Shorts
          </h2>
          <p style={{
            margin: '2px 0 0 0',
            fontSize: isMobile ? '12px' : '14px',
            color: theme?.colors?.darkGray || '#666',
            fontWeight: '400'
          }}>Recommended for you</p>
        </div>

        {/* Split links: Lecture Clips + YouTube Shorts */}
        <div style={{ display: 'flex', gap: isMobile ? '12px' : '20px', alignItems: 'center' }}>
          {hasClips && (
            <Link to="/lecture-clips" style={linkStyle}>
              Lecture Clips
              <svg width="14" height="14" viewBox="0 0 24 24" fill={theme.colors.linkColor || theme.colors.primary}>
                <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
              </svg>
            </Link>
          )}
          {hasShorts && (
            <Link to="/shorts" style={linkStyle}>
              YouTube Shorts
              <svg width="14" height="14" viewBox="0 0 24 24" fill={theme.colors.linkColor || theme.colors.primary}>
                <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
              </svg>
            </Link>
          )}
          {!hasClips && !hasShorts && (
            <Link to="/shorts" style={linkStyle}>
              View all
              <svg width="14" height="14" viewBox="0 0 24 24" fill={theme.colors.linkColor || theme.colors.primary}>
                <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
              </svg>
            </Link>
          )}
        </div>
      </div>

      {/* Shorts Container Frame */}
      <section style={{
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 251, 0.95) 100%)',
        borderRadius: isMobile ? '12px' : '16px',
        boxShadow: isMobile ? '0 4px 20px rgba(0, 0, 0, 0.06)' : '0 10px 40px rgba(0, 0, 0, 0.08), 0 2px 10px rgba(0, 0, 0, 0.04)',
        padding: isMobile ? '16px' : '24px',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.6)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Gradient overlay decoration */}
        <div style={{
          position: 'absolute',
          top: '-50%',
          right: '-10%',
          width: '300px',
          height: '300px',
          background: `radial-gradient(circle, ${theme.colors.primary}15 0%, transparent 70%)`,
          borderRadius: '50%',
          pointerEvents: 'none',
          zIndex: 0
        }} />
      
      <div style={{
        position: 'relative',
        padding: isMobile ? '0' : '0 40px',
        width: '100%',
        overflow: 'hidden'
      }}>
        {/* Scroll buttons - only show on desktop */}
        {!isMobile && displayItems.length > 5 && (
          <>
            <button 
              onClick={scrollLeft}
              aria-label="Scroll left"
              style={{
                position: 'absolute',
                left: isMobile ? '-10px' : '0',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                width: isMobile ? '32px' : '40px',
                height: isMobile ? '32px' : '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 251, 0.95) 100%)',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: isMobile ? '16px' : '20px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                backdropFilter: 'blur(10px)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)';
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
              </svg>
            </button>
            
            <button 
              onClick={scrollRight}
              aria-label="Scroll right"
              style={{
                position: 'absolute',
                right: isMobile ? '-10px' : '0',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                width: isMobile ? '32px' : '40px',
                height: isMobile ? '32px' : '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 251, 0.95) 100%)',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: isMobile ? '16px' : '20px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                backdropFilter: 'blur(10px)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)';
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"/>
              </svg>
            </button>
          </>
        )}
        
        {/* Container - Grid on mobile, horizontal scroll on desktop */}
        <div 
          ref={rowRef}
          style={isMobile ? {
            // Mobile: 2x2 grid layout
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gridTemplateRows: 'repeat(2, 1fr)',
            gap: '12px',
            padding: '4px'
          } : {
            // Desktop: Horizontal scrolling
            display: 'flex',
            flexDirection: 'row',
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollBehavior: 'smooth',
            gap: '16px',
            padding: '8px 4px',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitScrollbar: 'none'
          }}
        >
          {displayItems.map((short, index) => (
            <div key={short.id || index} style={isMobile ? {
              // Mobile: Grid items
              width: '100%',
              aspectRatio: '9/16',
              position: 'relative'
            } : {
              // Desktop: Flex items
              flex: '0 0 160px',
              minWidth: '160px',
              maxWidth: '160px',
              transition: 'transform 0.3s ease'
            }}>
              <ShortCard 
                short={short} 
                isMobileGrid={isMobile}
              />
            </div>
          ))}
        </div>
      </div>
      </section>
    </div>
  );
};

export default ShortsPanel;